import { useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom-v5-compat';

import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { Backdrop } from 'dashboard/layouts/ShowViewLayoutV2';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { useDataContextSchema } from 'dashboard/lib/hooks/useDataContextSchema';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { AccountType, AccountTypeAttributes } from 'dashboard/models/account-type';
import { AccountModule, AccountTypeGridLayout } from 'dashboard/models/account-type-grid-layouts';
import { Configuration } from 'dashboard/models/grid-layout';
import { TransactionTypeStatus } from 'dashboard/models/transaction-type';
import { User } from 'dashboard/models/user';
import { showMessage } from 'dashboard/redux/actions/messages';
import { useListTransactionTypesQuery } from 'dashboard/redux/api/transaction-type';
import { getAllEnabledTemplates } from 'dashboard/redux/selectors/organization';
import { getPublishedVerificationTemplates } from 'dashboard/redux/selectors/verification-templates';
import {
  addAccountTypeGridLayout,
  clearAccountType,
  setAccountType,
  setAccountTypeGridLayout,
} from 'dashboard/redux/slices/account-type';
import { RootState } from 'dashboard/redux/store';
import {
  addAccountStatus,
  archiveAccountStatus,
  loadAccountStatuses,
  renameAccountStatus,
  restoreAccountStatus,
} from 'dashboard/redux/thunk-actions/account-statuses';
import {
  loadAccountType,
  setDefaultAccountTypeStatus,
} from 'dashboard/redux/thunk-actions/account-type';
import { loadAccountTypes } from 'dashboard/redux/thunk-actions/account-types';
import { loadTransactionTypesFilterOptions } from 'dashboard/redux/thunk-actions/transaction-types';
import { loadVerificationTemplatesList } from 'dashboard/redux/thunk-actions/verification-templates';
import { useObjectSchemas } from 'dashboard/routes/DashboardAccountTypesShowRoute/useObjectSchemas';
import DashboardAccountTypesShowView from 'dashboard/views/DashboardAccountTypesShowView/DashboardAccountTypesShowView';
import { fieldSchemaErrorMessage, isFieldSchemaError } from 'lib/fields-errors';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useOnMountAndUnmount from 'lib/hooks/useOnMountAndUnmount';
import usePermission from 'lib/hooks/usePermission';
import useRTKErrorHandler from 'lib/hooks/useRTKErrorHandler';
import { ApiModel } from 'lib/interfaces/api';
import { FeatureFlagsMap } from 'lib/interfaces/feature-flags';
import { Permission } from 'lib/permissions';
import {
  conflictErrorString,
  isConflictError,
  isPermissionsError,
  permissionsErrorMessage,
} from 'lib/permissions-errors';

interface DashboardAccountTypesShowRouteProps {
  currentUser: User;
  featureFlags: FeatureFlagsMap;
}

type Params = { accountTypeId: string };

const DashboardAccountTypesShowRoute = (
  props: DashboardAccountTypesShowRouteProps & PropsFromRedux,
) => {
  useOnMountAndUnmount(props.clearAccountType);
  const params = useParams<Params>() as Params;

  const hasVerificationTemplateViewPermission = usePermission(Permission.VerificationTemplateView);

  const layoutVariables = useMemo(
    () => props.accountVariableDefinitions.map((v) => v.attributes['variable-definition']),
    [props.accountVariableDefinitions],
  );

  const { getObjectSchema } = useObjectSchemas();
  const { dataSchema } = useDataContextSchema(layoutVariables, getObjectSchema);

  const { callback: handleLoadAccountType } = useApiLoader({
    loader: () => props.loadAccountType(params.accountTypeId),
    deps: [props.loadAccountType, params.accountTypeId],
    skip: false,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load account type fields.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const {
    callback: updateAccountType,
    loading: isUpdatingAccountType,
    error: updateAccountTypeError,
  } = useApiCallback(
    async (attributes: AccountTypeAttributes, deactivateFieldSchemaKeys: string[]) => {
      const id = params.accountTypeId;

      await SessionClientDashboard.updateResource<AccountType>(
        {
          id: id,
          attributes: {
            'field-schemas': attributes['field-schemas'],
            'relation-schemas': attributes['relation-schemas'],
            identifiers: attributes.identifiers,
            name: attributes.name,
          },
          type: ApiModel.AccountType,
        },
        {
          meta: {
            'deactivate-field-schema-keys': deactivateFieldSchemaKeys,
          },
        },
      );
      await handleLoadAccountType();
      props.showMessage('Successfully saved account type.', MessageType.Success, true);
    },

    [props.currentEnvironment, props.showMessage],
  );

  useApiErrorHandler({
    error: updateAccountTypeError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else if (isConflictError(error)) {
        props.showMessage(conflictErrorString(error), MessageType.Failure);
      } else if (isFieldSchemaError(error)) {
        props.showMessage(fieldSchemaErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to save account type.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { callback: createAccountTypeGridLayout, error: createAccountTypeGridLayoutError } =
    useApiCallback(
      async (configuration: Configuration<AccountModule>) => {
        const response = await SessionClientDashboard.postResourceCreate<
          AccountTypeGridLayout,
          {
            type: ApiModel.AccountTypeGridLayout;
            attributes: {
              'account-type-id': string;
              name: string;
              configuration: Configuration<AccountModule>;
            };
          }
        >({
          type: ApiModel.AccountTypeGridLayout,
          attributes: {
            'account-type-id': params.accountTypeId,
            name: 'Default',
            configuration,
          },
        });

        props.addAccountTypeGridLayout(response.data);
        // as far as users are concerned, they've just updated the default layout
        props.showMessage('Successfully updated layout.', MessageType.Success);
      },
      [props.showMessage],
    );

  useApiErrorHandler({
    error: createAccountTypeGridLayoutError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to save layout.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { callback: updateAccountTypeGridLayout, error: updateAccountTypeGridLayoutError } =
    useApiCallback(
      async (gridLayout: AccountTypeGridLayout) => {
        const response =
          await SessionClientDashboard.updateResource<AccountTypeGridLayout>(gridLayout);

        props.setAccountTypeGridLayout(response.data);
        props.showMessage('Successfully updated layout.', MessageType.Success);
      },
      [props.showMessage],
    );

  useApiErrorHandler({
    error: updateAccountTypeGridLayoutError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to save layout.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: isLoadingAccountStatuses, callback: _ } = useApiLoader({
    loader: () => props.loadAccountStatuses(params.accountTypeId),
    deps: [params.accountTypeId, props.loadAccountStatuses],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load account statuses.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const {
    loading: isAdding,
    error: handleAddAccountStatusError,
    callback: handleAddAccountStatus,
  } = useApiCallback(async (name: string) => {
    await props.addAccountStatus(name, params.accountTypeId);
    props.showMessage('Successfully added new account status.', MessageType.Success);
  }, []);

  useApiErrorHandler({
    error: handleAddAccountStatusError,
    onError: () => {
      props.showMessage(
        'Failed to add new account status, please try again.',
        MessageType.Failure,
        false,
      );
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const {
    loading: isArchiving,
    error: handleArchiveAccountStatusError,
    callback: handleArchiveAccountStatus,
  } = useApiCallback(async (name: string) => {
    await props.archiveAccountStatus(name);
    props.showMessage('Successfully archived account status.', MessageType.Success);
  }, []);

  useApiErrorHandler({
    error: handleArchiveAccountStatusError,
    onError: () => {
      props.showMessage(
        'Accounts exist with this status. Update those accounts to a different status and try again.',
        MessageType.Failure,
        false,
      );
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const {
    loading: isRestoring,
    error: handleRestoreAccountStatusError,
    callback: handleRestoreAccountStatus,
  } = useApiCallback(async (accountStatusId: string) => {
    await props.restoreAccountStatus(accountStatusId);
    props.showMessage('Successfully restored account status.', MessageType.Success);
  }, []);

  useApiErrorHandler({
    error: handleRestoreAccountStatusError,
    onError: () => {
      props.showMessage(
        'Failed to restore account status, please try again.',
        MessageType.Failure,
        false,
      );
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const {
    loading: isRenaming,
    error: handleRenameAccountStatusError,
    callback: handleRenameAccountStatus,
  } = useApiCallback(async (accountStatusId: string, name: string) => {
    await props.renameAccountStatus(accountStatusId, name);
    props.showMessage('Successfully renamed account status.', MessageType.Success);
  }, []);

  useApiErrorHandler({
    error: handleRenameAccountStatusError,
    onError: () => {
      props.showMessage(
        'Failed to rename new account status, please try again.',
        MessageType.Failure,
        false,
      );
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const {
    loading: isSettingDefault,
    error: handleSetDefaultAccountStatusError,
    callback: handleSetDefaultAccountStatus,
  } = useApiCallback(
    async (accountStatusId: string) => {
      if (!props.accountType) {
        props.showMessage('Failed to set default account status.', MessageType.Failure);
        return;
      }

      await props.setDefaultAccountTypeStatus(props.accountType.id, accountStatusId);
      props.showMessage('Successfully set default account status.', MessageType.Success);
    },
    [props.accountType],
  );

  useApiErrorHandler({
    error: handleSetDefaultAccountStatusError,
    onError: () => {
      props.showMessage(
        'Failed to set default account status, please try again.',
        MessageType.Failure,
        false,
      );
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: isLoadingAccountTypes } = useApiLoader({
    loader: async () => {
      // TODO: only load active account types
      await props.loadAccountTypes();
    },
    deps: [props.loadAccountTypes],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load account types', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const {
    isLoading: isLoadingTransactionTypes,
    data: { data: transactionTypes = [] } = {},
    error: transactionTypesError,
  } = useListTransactionTypesQuery({ filter: { status: [TransactionTypeStatus.Active] } });

  useRTKErrorHandler({
    error: transactionTypesError,
    onError: () => ({ message: 'Failed to load Transaction Types.', level: MessageType.Failure }),
  });

  useApiLoader({
    loader: () =>
      props.loadVerificationTemplatesList({
        currentPage: 1,
        pageSize: 100,
        sortParams: { sort: '-', shouldSortDescending: false },
        filters: {},
      }),
    deps: [props.loadVerificationTemplatesList],
    skip: !hasVerificationTemplateViewPermission,
    onError: (error) => {
      if (!isPermissionsError(error)) {
        props.showMessage('Failed to load verification templates.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const isLoadingRelationSchemaType = isLoadingAccountTypes || isLoadingTransactionTypes;

  return !isLoadingRelationSchemaType && props.accountType?.attributes ? (
    <DashboardAccountTypesShowView
      organization={props.organization}
      currentEnvironment={props.currentEnvironment}
      accountStatuses={props.accountStatuses}
      accountType={props.accountType}
      accountTypes={props.accountTypes}
      graph={props.graph}
      graphQueryTemplates={props.graphQueryTemplates}
      transactionTypes={transactionTypes}
      gridLayouts={props.gridLayouts}
      dataSchema={dataSchema}
      enabledInquiryTemplates={props.enabledTemplates}
      publishedVerificationTemplates={props.publishedVerificationTemplates}
      onUpdateAccountType={updateAccountType}
      onUpdateAccountTypeGridLayout={updateAccountTypeGridLayout}
      showMessage={props.showMessage}
      handleAddAccountStatus={handleAddAccountStatus}
      handleArchiveAccountStatus={handleArchiveAccountStatus}
      handleRenameAccountStatus={handleRenameAccountStatus}
      handleRestoreAccountStatus={handleRestoreAccountStatus}
      handleSetDefaultAccountStatus={handleSetDefaultAccountStatus}
      onCreateAccountTypeGridLayout={createAccountTypeGridLayout}
      isSubmittingStatuses={
        isAdding || isArchiving || isRenaming || isRestoring || isSettingDefault
      }
      isLoadingStatuses={isLoadingAccountStatuses}
      isUpdatingAccountType={isUpdatingAccountType}
    />
  ) : (
    <Backdrop>
      <LoadingIndicator fullscreen={true} />
    </Backdrop>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    accountType: state.accountType.accountType,
    accountTypes: state.accountTypes.accountTypes,
    accountStatuses: state.accountStatuses.accountStatuses,
    graph: state.graph.graph,
    graphQueryTemplates: state.graphQueryTemplates.graphQueryTemplates,
    gridLayouts: state.accountType.gridLayouts,
    accountVariableDefinitions: state.accountType.accountVariableDefinitions,
    environments: state.organization.environments,
    enabledTemplates: getAllEnabledTemplates(state),
    publishedVerificationTemplates: getPublishedVerificationTemplates(state),
    organization: state.organization.organization,
    currentEnvironment: state.currentEnvironment.currentEnvironment,
  };
};
const mapDispatchToProps = {
  addAccountStatus,
  addAccountTypeGridLayout,
  archiveAccountStatus,
  clearAccountType,
  loadAccountType,
  loadAccountStatuses,
  loadAccountTypes,
  loadTransactionTypesFilterOptions,
  loadVerificationTemplatesList,
  renameAccountStatus,
  restoreAccountStatus,
  setDefaultAccountTypeStatus,
  setAccountType,
  setAccountTypeGridLayout,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardAccountTypesShowRoute);
